'use strict';

var _classCallCheck = require("/home/circleci/repo/projects/idea-hub-client/node_modules/@babel/runtime/helpers/classCallCheck");

var _createClass = require("/home/circleci/repo/projects/idea-hub-client/node_modules/@babel/runtime/helpers/createClass");

var multistream = require('multistream-select');

var Connection = require('interface-connection').Connection;

var setImmediate = require('async/setImmediate');

var Circuit = require('libp2p-circuit');

var waterfall = require('async/waterfall');

var debug = require('debug');

var log = debug('libp2p:switch:dial');

var getPeerInfo = require('./get-peer-info');

var observeConnection = require('./observe-connection');

var UNEXPECTED_END = 'Unexpected end of input from reader.';
/**
 * Uses the given MultistreamDialer to select the protocol matching the given key
 *
 * A helper method to catch errors from pull streams ending unexpectedly
 * Needed until https://github.com/dignifiedquire/pull-length-prefixed/pull/8 is merged.
 *
 * @param {MultistreamDialer} msDialer a multistream.Dialer
 * @param {string} key The key type to select
 * @param {function(Error)} callback Used for standard async flow
 * @param {function(Error)} abort A callback to be used for ending the connection outright
 * @returns {void}
 */

function selectSafe(msDialer, key, callback, abort) {
  msDialer.select(key, function (err, conn) {
    if (err === true) {
      return abort(new Error(UNEXPECTED_END));
    }

    callback(err, conn);
  });
}
/**
 * Uses the given MultistreamDialer to handle the given connection
 *
 * A helper method to catch errors from pull streams ending unexpectedly
 * Needed until https://github.com/dignifiedquire/pull-length-prefixed/pull/8 is merged
 *
 * @param {MultistreamDialer} msDialer
 * @param {Connection} connection The connection to handle
 * @param {function(Error)} callback Used for standard async flow
 * @param {function(Error)} abort A callback to be used for ending the connection outright
 * @returns {void}
 */


function handleSafe(msDialer, connection, callback, abort) {
  msDialer.handle(connection, function (err) {
    // Repackage errors from pull-streams ending unexpectedly.
    // Needed until https://github.com/dignifiedquire/pull-length-prefixed/pull/8 is merged.
    if (err === true) {
      return abort(new Error(UNEXPECTED_END));
    }

    callback(err);
  });
}
/**
 * Manages dialing to another peer, including muxer upgrades
 * and crypto management. The main entry point for dialing is
 * Dialer.dial
 *
 * @param {Switch} _switch
 * @param {PeerInfo} peerInfo
 * @param {string} protocol
 * @param {function(Error, Connection)} callback
 */


var Dialer =
/*#__PURE__*/
function () {
  function Dialer(_switch, peerInfo, ourPeerInfo, protocol, callback) {
    _classCallCheck(this, Dialer);

    this.switch = _switch;
    this.peerInfo = peerInfo;
    this.ourPeerInfo = ourPeerInfo;
    this.protocol = protocol;
    this.callback = callback;
  }
  /**
   * Initializes a proxy connection and returns it. The connection is also immediately
   * dialed. This will include establishing the base connection, crypto, muxing and the
   * protocol handshake if all needed components have already been set.
   *
   * @returns {Connection}
   */


  _createClass(Dialer, [{
    key: "dial",
    value: function dial() {
      var _this = this;

      var proxyConnection = new Connection();
      proxyConnection.setPeerInfo(this.peerInfo);
      waterfall([function (cb) {
        _this._establishConnection(cb);
      }, function (connection, cb) {
        if (connection) {
          proxyConnection.setPeerInfo(_this.peerInfo);
          proxyConnection.setInnerConn(connection);
          return cb(null, proxyConnection);
        }

        cb(null);
      }], function (err, connection) {
        if (err && err.message === UNEXPECTED_END || err === true) {
          log('Connection dropped for %s', _this.peerInfo.id.toB58String());
          return _this.callback(null, null);
        }

        _this.callback(err, connection);
      });
      return proxyConnection;
    }
    /**
     * Establishes a base connection and then continues to upgrade that connection
     * including: crypto, muxing and the protocol handshake. If any upgrade is not
     * yet available, or already exists, the upgrade will continue where it left off.
     *
     * @private
     * @param {function(Error, Connection)} callback
     * @returns {void}
     */

  }, {
    key: "_establishConnection",
    value: function _establishConnection(callback) {
      var _this2 = this;

      var b58Id = this.peerInfo.id.toB58String();
      log('dialing %s', b58Id);

      if (b58Id === this.ourPeerInfo.id.toB58String()) {
        return callback(new Error('A node cannot dial itself'));
      }

      waterfall([function (cb) {
        // Start with a base connection, which includes encryption
        _this2._createBaseConnection(b58Id, cb);
      }, function (baseConnection, cb) {
        // Upgrade the connection with a muxer
        _this2._createMuxedConnection(baseConnection, b58Id, cb);
      }, function (muxer, cb) {
        // If we have no protocol, dont continue with the handshake
        if (!_this2.protocol) {
          return cb();
        } // If we have a muxer, create a new stream, otherwise it's a standard connection


        if (muxer.newStream) {
          muxer.newStream(function (err, conn) {
            if (err) return cb(err);

            _this2._performProtocolHandshake(conn, cb);
          });
          return;
        }

        _this2._performProtocolHandshake(muxer, cb);
      }], function (err, connection) {
        callback(err, connection);
      });
    }
    /**
     * If the base connection already exists to the PeerId key, `b58Id`,
     * it will be returned in the callback. If no connection exists, one will
     * be attempted via Dialer.attemptDial.
     *
     * @private
     * @param {string} b58Id
     * @param {function(Error, Connection)} callback
     * @returns {void}
     */

  }, {
    key: "_createBaseConnection",
    value: function _createBaseConnection(b58Id, callback) {
      var _this3 = this;

      var baseConnection = this.switch.conns[b58Id];
      var muxedConnection = this.switch.muxedConns[b58Id]; // if the muxed connection exists, dont return a connection,
      // _createMuxedConnection will get the connection

      if (muxedConnection) {
        return callback(null, null);
      }

      if (baseConnection) {
        this.switch.conns[b58Id] = undefined;
        return callback(null, baseConnection);
      }

      waterfall([function (cb) {
        _this3._attemptDial(cb);
      }, function (baseConnection, cb) {
        // Create a private connection if it's needed
        _this3._createPrivateConnection(baseConnection, cb);
      }, function (connection, cb) {
        // Add the Switch's crypt encryption to the connection
        _this3._encryptConnection(connection, cb);
      }], function (err, encryptedConnection) {
        if (err) {
          return callback(err);
        }

        callback(null, encryptedConnection);
      });
    }
    /**
     * If the switch has a private network protector, `switch.protector`, its `protect`
     * method will be called with the given connection. The resulting, wrapped connection
     * will be returned via the callback.
     *
     * @param {Connection} connection The connection to protect
     * @param {function(Error, Connection)} callback
     * @returns {void}
     */

  }, {
    key: "_createPrivateConnection",
    value: function _createPrivateConnection(connection, callback) {
      var _this4 = this;

      if (this.switch.protector === null) {
        return callback(null, connection);
      } // If the switch has a protector, be private


      var protectedConnection = this.switch.protector.protect(connection, function (err) {
        if (err) {
          return callback(err);
        }

        protectedConnection.setPeerInfo(_this4.peerInfo);
        callback(null, protectedConnection);
      });
    }
    /**
     * If the given PeerId key, `b58Id`, has an existing muxed connection
     * it will be returned via the callback, otherwise the connection
     * upgrade will be initiated via Dialer.attemptMuxerUpgrade.
     *
     * @private
     * @param {Connection} connection
     * @param {string} b58Id
     * @param {function(Error, Connection)} callback
     * @returns {void}
     */

  }, {
    key: "_createMuxedConnection",
    value: function _createMuxedConnection(connection, b58Id, callback) {
      var _this5 = this;

      var muxedConnection = this.switch.muxedConns[b58Id];

      if (muxedConnection) {
        return callback(null, muxedConnection.muxer);
      }

      connection.setPeerInfo(this.peerInfo);

      this._attemptMuxerUpgrade(connection, b58Id, function (err, muxer) {
        if (err && !_this5.protocol) {
          _this5.switch.conns[b58Id] = connection;
          return callback(null, null);
        }

        if (err) {
          log('muxer upgrade failed with error', err); // couldn't upgrade to Muxer, it is ok, use the existing connection

          return callback(null, connection);
        }

        callback(null, muxer);
      }, callback);
    }
    /**
     * Iterates over each Muxer on the Switch and attempts to upgrade
     * the given `connection`. Successful muxed connections will be stored
     * on the Switch.muxedConns with `b58Id` as their key for future reference.
     *
     * @private
     * @param {Connection} connection
     * @param {string} b58Id
     * @param {function(Error, Connection)} callback
     * @param {function(Error, Connection)} abort A callback to be used for ending the connection outright
     * @returns {void}
     */

  }, {
    key: "_attemptMuxerUpgrade",
    value: function _attemptMuxerUpgrade(connection, b58Id, callback, abort) {
      var _this6 = this;

      var muxers = Object.keys(this.switch.muxers);

      if (muxers.length === 0) {
        return callback(new Error('no muxers available'));
      }

      var msDialer = new multistream.Dialer();
      handleSafe(msDialer, connection, function (err) {
        if (err) {
          return callback(new Error('multistream not supported'));
        } // 1. try to handshake in one of the muxers available
        // 2. if succeeds
        //  - add the muxedConn to the list of muxedConns
        //  - add incomming new streams to connHandler


        var nextMuxer = function nextMuxer(key) {
          log('selecting %s', key);
          selectSafe(msDialer, key, function (err, _conn) {
            if (err) {
              if (muxers.length === 0) {
                return callback(new Error('could not upgrade to stream muxing'));
              }

              return nextMuxer(muxers.shift());
            } // observe muxed connections


            var conn = observeConnection(null, key, _conn, _this6.switch.observer);

            var muxedConn = _this6.switch.muxers[key].dialer(conn);

            _this6.switch.muxedConns[b58Id] = {
              muxer: muxedConn
            };
            muxedConn.once('close', function () {
              delete _this6.switch.muxedConns[b58Id];

              _this6.peerInfo.disconnect();

              _this6.switch._peerInfo.disconnect();

              log("closed connection to ".concat(b58Id));
              setImmediate(function () {
                return _this6.switch.emit('peer-mux-closed', _this6.peerInfo);
              });
            }); // For incoming streams, in case identify is on

            muxedConn.on('stream', function (conn) {
              conn.setPeerInfo(_this6.peerInfo);

              _this6.switch.protocolMuxer(null)(conn);
            });
            setImmediate(function () {
              return _this6.switch.emit('peer-mux-established', _this6.peerInfo);
            });
            callback(null, muxedConn);
          }, abort);
        };

        nextMuxer(muxers.shift());
      }, abort);
    }
    /**
     * Iterates over each Transport on the Switch and attempts to connect
     * to the peer. Once a Transport succeeds, no additional Transports will
     * be dialed.
     *
     * @private
     * @param {function(Error, Connection)} callback
     * @returns {void}
     */

  }, {
    key: "_attemptDial",
    value: function _attemptDial(callback) {
      var _this7 = this;

      if (!this.switch.hasTransports()) {
        return callback(new Error('No transports registered, dial not possible'));
      }

      var tKeys = this.switch.availableTransports(this.peerInfo);
      var circuitEnabled = Boolean(this.switch.transports[Circuit.tag]);
      var circuitTried = false;

      var nextTransport = function nextTransport(key) {
        var transport = key;

        var b58Id = _this7.peerInfo.id.toB58String();

        if (!transport) {
          if (!circuitEnabled) {
            var msg = "Circuit not enabled and all transports failed to dial peer ".concat(b58Id, "!");
            return callback(new Error(msg));
          }

          if (circuitTried) {
            return callback(new Error("No available transports to dial peer ".concat(b58Id, "!")));
          }

          log("Falling back to dialing over circuit");

          _this7.peerInfo.multiaddrs.add("/p2p-circuit/ipfs/".concat(b58Id));

          circuitTried = true;
          transport = Circuit.tag;
        }

        log("dialing transport ".concat(transport));

        _this7.switch.transport.dial(transport, _this7.peerInfo, function (err, _conn) {
          if (err) {
            log(err);
            return nextTransport(tKeys.shift());
          }

          var conn = observeConnection(transport, null, _conn, _this7.switch.observer);
          callback(null, conn);
        });
      };

      nextTransport(tKeys.shift());
    }
    /**
     * Attempts to encrypt the given `connection` with the Switch's crypto.
     *
     * @private
     * @param {Connection} connection
     * @param {function(Error, Connection)} callback
     * @returns {void}
     */

  }, {
    key: "_encryptConnection",
    value: function _encryptConnection(connection, callback) {
      var _this8 = this;

      var msDialer = new multistream.Dialer();
      handleSafe(msDialer, connection, function (err) {
        if (err) {
          return callback(err);
        }

        var myId = _this8.switch._peerInfo.id;
        log('selecting crypto: %s', _this8.switch.crypto.tag);
        selectSafe(msDialer, _this8.switch.crypto.tag, function (err, _conn) {
          if (err) {
            return callback(err);
          }

          var conn = observeConnection(null, _this8.switch.crypto.tag, _conn, _this8.switch.observer);

          var encryptedConnection = _this8.switch.crypto.encrypt(myId, conn, _this8.peerInfo.id, function (err) {
            if (err) {
              return callback(err);
            }

            encryptedConnection.setPeerInfo(_this8.peerInfo);
            callback(null, encryptedConnection);
          });
        }, callback);
      }, callback);
    }
    /**
     * Initiates a handshake for the Dialer's set protocol
     *
     * @private
     * @param {Connection} connection
     * @param {function(Error, Connection)} callback
     * @returns {void}
     */

  }, {
    key: "_performProtocolHandshake",
    value: function _performProtocolHandshake(connection, callback) {
      var _this9 = this;

      // If there is no protocol set yet, don't perform the handshake
      if (!this.protocol) {
        callback();
      }

      var msDialer = new multistream.Dialer();
      handleSafe(msDialer, connection, function (err) {
        if (err) {
          return callback(err);
        }

        selectSafe(msDialer, _this9.protocol, function (err, _conn) {
          if (err) {
            log("could not perform protocol handshake: ", err);
            return callback(err);
          }

          var conn = observeConnection(null, _this9.protocol, _conn, _this9.switch.observer);
          callback(null, conn);
        }, callback);
      }, callback);
    }
  }]);

  return Dialer;
}();
/**
 * Returns a Dialer generator that when called, will immediately begin dialing
 * fo the given `peer`.
 *
 * @param {Switch} _switch
 * @returns {function(PeerInfo, string, function(Error, Connection))}
 */


function dial(_switch) {
  /**
   * Creates a new dialer and immediately begins dialing to the given `peer`
   *
   * @param {PeerInfo} peer
   * @param {string} protocol
   * @param {function(Error, Connection)} callback
   * @returns {Connection}
   */
  return function (peer, protocol, callback) {
    if (typeof protocol === 'function') {
      callback = protocol;
      protocol = null;
    }

    callback = callback || function noop() {};

    var peerInfo = getPeerInfo(peer, _switch._peerBook);
    var dialer = new Dialer(_switch, peerInfo, _switch._peerInfo, protocol, callback);
    return dialer.dial();
  };
}

module.exports = dial;